/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
/**
 * SVG Path Reader
 * for further information see:
 * https://www.w3schools.com/graphics/svg_path.asp
 *
 */
export class SVGPathReader {
    constructor(path, x = 0, y = 0) {
        this._moveTo = () => {
            this._x = this._getNumber(this._x);
            this._y = this._getNumber(this._y);
            this._path.moveTo(this._x, this._y);
            this._xStart = this._x;
            this._yStart = this._y;
            this._current.func = this._lineTo;
            this._current.lineMode = 3;
        };
        this._lineToFirst = () => {
            if (this._xStart !== this._x || this._yStart !== this._y) {
                this._path.lineTo(this._xStart, this._yStart);
            }
        };
        // tslint:disable:no-bitwise
        this._lineTo = () => {
            this._xPre = this._x;
            this._yPre = this._y;
            this._x = ((this._current.lineMode & 2) === 0) ? this._x : this._getNumber(this._x);
            this._y = ((this._current.lineMode & 1) === 0) ? this._y : this._getNumber(this._y);
            this._path.lineTo(this._x, this._y);
        };
        this._bezierCurveTo = () => {
            const xB1 = this._current.short ? (2 * this._x - this._xPre) : this._getNumber(this._x);
            const yB1 = this._current.short ? (2 * this._y - this._yPre) : this._getNumber(this._y);
            this._xPre = this._getNumber(this._x);
            this._yPre = this._getNumber(this._y);
            this._x = this._getNumber(this._x);
            this._y = this._getNumber(this._y);
            this._path.bezierCurveTo(xB1, yB1, this._xPre, this._yPre, this._x, this._y);
        };
        this._quadraticCurveTo = () => {
            this._xPre = this._current.short ? (2 * this._x - this._xPre) : this._getNumber(this._x);
            this._yPre = this._current.short ? (2 * this._y - this._yPre) : this._getNumber(this._y);
            this._x = this._getNumber(this._x);
            this._y = this._getNumber(this._y);
            this._path.quadraticCurveTo(this._xPre, this._yPre, this._x, this._y);
        };
        this._ellipticalArc = () => {
            let rx = Math.abs(this._parseNumber());
            let ry = Math.abs(this._parseNumber());
            const xar = (this._parseNumber() % 360.0) * (Math.PI / 180.0);
            const laf = this._parseNumber() !== 0; // long arc flag
            const sf = this._parseNumber() !== 0; // sweep flag
            const nx = this._getNumber(this._x);
            const ny = this._getNumber(this._y);
            const c = Math.cos(xar);
            const s = Math.sin(xar);
            const dx2 = (this._x - nx) / 2.0;
            const dy2 = (this._y - ny) / 2.0;
            const x1 = (c * dx2 + s * dy2);
            const y1 = (-s * dx2 + c * dy2);
            const x1Sq = x1 * x1;
            const y1Sq = y1 * y1;
            let rxSq = rx * rx;
            let rySq = ry * ry;
            const radiusTest = x1Sq / rxSq + y1Sq / rySq;
            if (radiusTest > 1) {
                rx = Math.sqrt(radiusTest) * rx;
                ry = Math.sqrt(radiusTest) * ry;
                rxSq = rx * rx;
                rySq = ry * ry;
            }
            let sq = ((rxSq * rySq) - (rxSq * y1Sq) - (rySq * x1Sq)) / ((rxSq * y1Sq) + (rySq * x1Sq));
            sq = (sq < 0) ? 0 : sq;
            const coef = ((laf === sf) ? -1 : 1) * Math.sqrt(sq);
            const cx1 = coef * ((rx * y1) / ry);
            const cy1 = coef * -((ry * x1) / rx);
            const sx2 = (this._x + nx) / 2.0;
            const sy2 = (this._y + ny) / 2.0;
            const cx = sx2 + (c * cx1 - s * cy1);
            const cy = sy2 + (s * cx1 + c * cy1);
            const ux = (x1 - cx1) / rx;
            const uy = (y1 - cy1) / ry;
            const vx = (-x1 - cx1) / rx;
            const vy = (-y1 - cy1) / ry;
            const startAng = ((uy < 0) ? -1 : 1) * Math.acos(ux / Math.sqrt((ux * ux) + (uy * uy)));
            let deltaAng = ((ux * vy - uy * vx < 0) ? -1 : 1) * Math.acos((ux * vx + uy * vy) / Math.sqrt((ux * ux + uy * uy) * (vx * vx + vy * vy)));
            if (!sf && deltaAng > 0) {
                deltaAng -= Math.PI * 2;
            }
            else if (sf && deltaAng < 0) {
                deltaAng += Math.PI * 2;
            }
            this._path.absellipse(cx, cy, rx, ry, startAng, startAng + deltaAng, !sf, xar);
            this._x = nx;
            this._y = ny;
        };
        // tslint:disable-next-line:member-ordering
        this._commandList = new Map([
            ['m', { rel: true, func: this._moveTo }],
            ['M', { func: this._moveTo }],
            ['Z', { noRepeat: true, func: this._lineToFirst }],
            ['z', { noRepeat: true, func: this._lineToFirst }],
            ['l', { rel: true, lineMode: 3, func: this._lineTo }],
            ['h', { rel: true, lineMode: 2, func: this._lineTo }],
            ['v', { rel: true, lineMode: 1, func: this._lineTo }],
            ['L', { lineMode: 3, func: this._lineTo }],
            ['H', { lineMode: 2, func: this._lineTo }],
            ['V', { lineMode: 1, func: this._lineTo }],
            ['c', { rel: true, func: this._bezierCurveTo }],
            ['s', { rel: true, short: true, func: this._bezierCurveTo }],
            ['C', { func: this._bezierCurveTo }],
            ['S', { short: true, func: this._bezierCurveTo }],
            ['q', { rel: true, func: this._quadraticCurveTo }],
            ['t', { rel: true, short: true, func: this._quadraticCurveTo }],
            ['Q', { func: this._quadraticCurveTo }],
            ['T', { short: true, func: this._quadraticCurveTo }],
            ['A', { func: this._ellipticalArc }],
            ['a', { rel: true, func: this._ellipticalArc }],
        ]);
        this._path = path;
        this._x = x;
        this._y = y;
    }
    readSVGPath(svgPath) {
        this._index = 0;
        this._svgPath = svgPath;
        while (this._index < svgPath.length) {
            let currentCommand = ' ';
            while (currentCommand === ' ' && this._index < svgPath.length) {
                currentCommand = this._svgPath[this._index++];
            }
            const obj = this._commandList.get(currentCommand);
            this._current = obj;
            if (obj.func) {
                obj.func();
                while (!obj.noRepeat && this._nextIsNumber()) {
                    obj.func();
                }
            }
        }
    }
    _skip() {
        let c;
        while (this._index < this._svgPath.length) {
            c = this._svgPath[this._index];
            if (c !== ',' && c !== ' ') {
                break;
            }
            this._index++;
        }
        return c;
    }
    _nextIsNumber() {
        const c = this._skip();
        const re = /[0-9.-]/;
        return re.test(c);
    }
    _parseNumber() {
        let c = this._skip();
        const indexStart = this._index;
        if (c === '-') {
            this._index++;
        }
        const re = /[0-9.]/;
        while (this._index < this._svgPath.length) {
            c = this._svgPath[this._index];
            if (re.test(c)) {
                this._index++;
            }
            else {
                break;
            }
        }
        const str = this._svgPath.substring(indexStart, this._index);
        return parseFloat(str);
    }
    _getNumber(relTo) {
        return this._parseNumber() + ((this._current.rel) ? relTo : 0);
    }
}
//# sourceMappingURL=SVGPathReader.js.map